import React from 'react'
import DatePicker from 'react-datepicker'
import { format, isValid, parse } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendar } from '@fortawesome/free-solid-svg-icons'
import { formatDateForDisplay } from 'sharedKernel/handlers'
import { trans } from 'sharedKernel/i18n'
import { IMeetingRoomSettingsDays } from 'domain/feature/meeting-room/shared/types'
import { isDateWorkingDay } from 'domain/feature/meeting-room/shared/workingDays'
import { PickerFieldShell } from './PickerFieldShell'

interface DatePickerFieldProps {
  id?: string
  value: string
  onChange: (value: string) => void
  minDate?: Date
  maxDate?: Date
  workingDays?: IMeetingRoomSettingsDays | null
  // PLA-3423: dia indisponível além dos não úteis (ex.: todo ocupado por reserva aprovada). Recebe "yyyy-MM-dd".
  isDateBlocked?: (date: string) => boolean
  onMonthChange?: (date: Date) => void
  isInvalid?: boolean
  placeholder?: string
}

export const DatePickerField = ({
  id,
  value,
  onChange,
  minDate,
  maxDate,
  workingDays,
  isDateBlocked,
  onMonthChange,
  isInvalid,
  placeholder = 'DD/MM/AAAA',
}: DatePickerFieldProps): JSX.Element => {
  const parsed = value ? parse(value, 'yyyy-MM-dd', new Date()) : null
  const selected = parsed != null && isValid(parsed) ? parsed : null
  const displayValue = formatDateForDisplay(value)
  const unavailableLabel = trans('meetingRoom.public.form.calendar.unavailable')

  const isUnavailable = (date: Date): boolean =>
    Boolean(workingDays && !isDateWorkingDay(date, workingDays)) ||
    Boolean(isDateBlocked?.(format(date, 'yyyy-MM-dd')))

  const handleChange = (date: Date | null, close: () => void): void => {
    if (date == null || isUnavailable(date)) return
    onChange(format(date, 'yyyy-MM-dd'))
    close()
  }

  return (
    <PickerFieldShell
      id={id}
      value={value}
      displayValue={displayValue}
      placeholder={placeholder}
      isInvalid={isInvalid}
      icon={<FontAwesomeIcon icon={faCalendar} fontSize={16} />}
    >
      {close => (
        <DatePicker
          locale={ptBR}
          selected={selected}
          onChange={date => handleChange(date, close)}
          minDate={minDate}
          maxDate={maxDate}
          onMonthChange={onMonthChange}
          filterDate={date => !isUnavailable(date)}
          dayClassName={date => (isUnavailable(date) ? 'res-day-unavailable' : '')}
          renderDayContents={((day: number, date?: Date) => {
            if (!date || !isUnavailable(date)) return day
            return (
              <span className="res-day-content" title={unavailableLabel} aria-label={unavailableLabel}>
                {day}
              </span>
            )
          }) as never}
          inline
          calendarStartDay={0}
        />
      )}
    </PickerFieldShell>
  )
}
